import React, { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

interface UserCreationFeedbackProps {
  open: boolean;
  status: number;
  message: string;
  userType?: string;
  userData?: any;
  onClose: () => void;
  onRetry?: () => void;
}

const UserCreationFeedback: React.FC<UserCreationFeedbackProps> = ({
  open,
  status,
  message,
  userType,
  userData,
  onClose,
  onRetry,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const isSuccess = status === 200;
  const isError = status >= 400;
  const isWarning = status >= 300 && status < 400;

  const getStatusIcon = () => {
    if (isSuccess)
      return <CheckCircleIcon className="w-16 h-16 text-green-500" />;
    if (isError) return <XCircleIcon className="w-16 h-16 text-red-500" />;
    return <ExclamationTriangleIcon className="w-16 h-16 text-yellow-500" />;
  };

  const getStatusColor = () => {
    if (isSuccess) return "text-green-600";
    if (isError) return "text-red-600";
    return "text-yellow-600";
  };

  const getStatusBgColor = () => {
    if (isSuccess) return "bg-green-50";
    if (isError) return "bg-red-50";
    return "bg-yellow-50";
  };

  const getStatusBorderColor = () => {
    if (isSuccess) return "border-green-200";
    if (isError) return "border-red-200";
    return "border-yellow-200";
  };

  const getUserTypeIcon = () => {
    switch (userType) {
      case "student":
        return <AcademicCapIcon className="w-6 h-6" />;
      case "teacher":
        return <UserIcon className="w-6 h-6" />;
      case "admin-school":
        return <BuildingOfficeIcon className="w-6 h-6" />;
      case "admin-startup":
        return <ShieldCheckIcon className="w-6 h-6" />;
      case "parent":
        return <UserGroupIcon className="w-6 h-6" />;
      default:
        return <UserIcon className="w-6 h-6" />;
    }
  };

  const getUserTypeLabel = () => {
    switch (userType) {
      case "student":
        return "Élève";
      case "teacher":
        return "Enseignant";
      case "admin-school":
        return "Administrateur d'école";
      case "admin-startup":
        return "Administrateur startup";
      case "parent":
        return "Parent";
      default:
        return "Utilisateur";
    }
  };

  const formatUserData = () => {
    if (!userData) return null;

    const data = [];

    if (userData.firstname && userData.lastname) {
      data.push({
        label: "Nom complet",
        value: `${userData.firstname} ${userData.lastname}`,
      });
    }

    if (userData.email) {
      data.push({
        label: "Email",
        value: userData.email,
      });
    }

    if (userData.phoneNumber) {
      data.push({
        label: "Téléphone",
        value: userData.phoneNumber,
      });
    }

    if (userData.schoolName) {
      data.push({
        label: "École",
        value: userData.schoolName,
      });
    }

    if (userData.matricule) {
      data.push({
        label: "Matricule",
        value: userData.matricule,
      });
    }

    if (userData.schoolClass) {
      data.push({
        label: "Classe",
        value: userData.schoolClass,
      });
    }

    return data;
  };

  const userDataFormatted = formatUserData();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      <div
        className={`p-4 md:p-6 ${getStatusBgColor()} border ${getStatusBorderColor()} rounded-lg`}
      >
        {/* Header avec animation */}
        <div className="text-center mb-6">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-white shadow-lg mb-4 ${
              isAnimating ? "animate-bounce" : ""
            }`}
          >
            {getStatusIcon()}
          </div>

          <h2
            className={`text-xl md:text-2xl font-bold mb-2 ${getStatusColor()}`}
          >
            {isSuccess
              ? "Utilisateur créé avec succès !"
              : isError
              ? "Erreur lors de la création"
              : "Attention"}
          </h2>

          <p className="text-gray-600 mb-4 text-sm md:text-base">{message}</p>

          {/* Type d'utilisateur */}
          {userType && (
            <div className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-white rounded-full shadow-sm">
              {getUserTypeIcon()}
              <span className="font-medium text-gray-700 text-sm md:text-base">
                {getUserTypeLabel()}
              </span>
            </div>
          )}
        </div>

        {/* Détails de l'utilisateur */}
        {isSuccess && userDataFormatted && userDataFormatted.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-semibold text-gray-800">
                Détails de l'utilisateur créé
              </h3>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
              >
                <EyeIcon className="w-4 h-4" />
                <span className="text-sm">
                  {showDetails ? "Masquer" : "Afficher"}
                </span>
              </button>
            </div>

            {showDetails && (
              <div className="bg-white rounded-lg p-3 md:p-4 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userDataFormatted.map((item, index) => (
                    <div key={index} className="space-y-1">
                      <label className="text-sm font-medium text-gray-600">
                        {item.label}
                      </label>
                      <p className="text-gray-800 font-medium">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
          {isError && onRetry && (
            <button
              onClick={onRetry}
              className="px-3 md:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm md:text-base"
            >
              Réessayer
            </button>
          )}

          <button
            onClick={onClose}
            className={`px-4 md:px-6 py-2 rounded-lg transition-colors text-sm md:text-base ${
              isSuccess
                ? "bg-[#131CC9] text-white hover:bg-[#0f1699]"
                : isError
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-yellow-600 text-white hover:bg-yellow-700"
            }`}
          >
            {isSuccess ? "Continuer" : "Fermer"}
          </button>
        </div>

        {/* Conseils pour les erreurs */}
        {isError && (
          <div className="mt-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2 text-sm md:text-base">
              Conseils pour résoudre le problème :
            </h4>
            <ul className="text-xs md:text-sm text-red-700 space-y-1">
              {status === 400 && (
                <>
                  <li>
                    • Vérifiez que tous les champs obligatoires sont remplis
                  </li>
                  <li>• Assurez-vous que l'email n'est pas déjà utilisé</li>
                  <li>
                    • Vérifiez le format de l'email et du numéro de téléphone
                  </li>
                </>
              )}
              {status === 409 && (
                <>
                  <li>• Cet utilisateur existe déjà dans le système</li>
                  <li>• Vérifiez l'email et le matricule</li>
                </>
              )}
              {status >= 500 && (
                <>
                  <li>• Problème temporaire du serveur</li>
                  <li>• Réessayez dans quelques instants</li>
                  <li>• Contactez l'administrateur si le problème persiste</li>
                </>
              )}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default UserCreationFeedback;
